import React, { memo } from 'react';
import { Handle, Position, NodeProps, useEdges } from 'reactflow';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  getCorFibraSplitter,
  getPadraoCoresSplitterLabel,
  type PadraoCoresSplitter,
} from '../../../utils/fiberColors';
import { useDiagramInteractive } from '../../../utils/useDiagramInteractive';

const HANDLE_PX = 12;
const HANDLE_OFFSET = -8;

const handleBaseStyle = {
  width: HANDLE_PX,
  height: HANDLE_PX,
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  flexShrink: 0,
} as const;

interface SplitterNodeData {  id: number;
  label: string;
  tipo: string;
  config: string;
  color: string;
  inputs: number;
  outputs: number;
  saida1_porcentagem?: number;
  saida2_porcentagem?: number;
  padrao_cores?: PadraoCoresSplitter;
  onDelete?: (id: number) => void;
  onEditSplitterPadrao?: (splitterId: number) => void;
  opticalSignal?: {    inputSignal: number | null;
    outputSignals: Record<string, number>;
  };
}

const SplitterNode: React.FC<NodeProps<SplitterNodeData>> = ({ data, selected, id }) => {
  const handleColor = data.color || '#2196f3';
  const edges = useEdges();
  const isDiagramInteractive = useDiagramInteractive();

  const outputsCount = typeof data.outputs === 'number' ? data.outputs : parseInt(String(data.outputs), 10) || 2;
  const is1x2 = outputsCount === 2;
  const padraoCores = data.padrao_cores ?? 'sem_padrao';
  const usaPadraoFibra = padraoCores === 'abnt' || padraoCores === 'eia598a';

  const outputGap = outputsCount <= 4 ? 10 : outputsCount <= 8 ? 6 : 5;
  const outputBandHeight =
    outputsCount <= 1 ? HANDLE_PX : (outputsCount - 1) * (HANDLE_PX + outputGap) + HANDLE_PX;
  // Margem extra do triângulo acima/abaixo das saídas (linhas vermelhas do print)
  const triangleExtraPad =
    outputsCount <= 2 ? 16 : outputsCount <= 4 ? 20 : outputsCount <= 8 ? 24 : outputsCount <= 16 ? 28 : 32;
  const topMargin = 6;
  const bottomMargin = 4;
  const padTop = topMargin + triangleExtraPad;
  const triangleTopPx = topMargin;
  const triangleBottomPx = padTop + outputBandHeight + triangleExtraPad;
  const nodeHeight = Math.min(
    Math.max(is1x2 ? 110 : triangleBottomPx + bottomMargin, 72),
    outputsCount > 12 ? 420 : Infinity,
  );
  const nodeWidth = Math.max(128, 96 + outputsCount * 1.5);
  const triangleTopPct = (triangleTopPx / nodeHeight) * 100;
  const triangleBottomPct = (triangleBottomPx / nodeHeight) * 100;
  const apexPct = ((triangleTopPx + triangleBottomPx) / 2 / nodeHeight) * 100;
  const trianglePoints = `0,${apexPct} 100,${triangleTopPct} 100,${triangleBottomPct}`;

  const isHandleConnected = (handleId: string, handleType: 'source' | 'target'): boolean =>
    edges.filter((edge) =>
      handleType === 'source'
        ? edge.source === id && edge.sourceHandle === handleId
        : edge.target === id && edge.targetHandle === handleId,
    ).length >= 5;

  const getCorHandleSaida = (index: number): { cor: string; isBranco: boolean } => {
    if (!usaPadraoFibra) {
      if (outputsCount === 2 && index === 1) return { cor: '#f44336', isBranco: false };
      return { cor: handleColor, isBranco: false };
    }
    const fibra = getCorFibraSplitter(index + 1, outputsCount, padraoCores);
    return { cor: fibra.cor, isBranco: fibra.nome === 'Branco' };
  };
  const getOutputTopPx = (index: number): number =>
    outputsCount <= 1 ? nodeHeight / 2 : padTop + index * (HANDLE_PX + outputGap) + HANDLE_PX / 2;

  const handleEditPadrao = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onEditSplitterPadrao?.(data.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onDelete?.(data.id);
  };

  const strokeColor = selected ? handleColor : handleColor;
  return (
    <Box
      sx={{
        position: 'relative',
        width: nodeWidth,
        height: nodeHeight,
        overflow: 'visible',
        filter: selected
          ? 'drop-shadow(0 3px 8px rgba(33,150,243,0.35))'
          : 'drop-shadow(0 2px 5px rgba(0,0,0,0.12))',
        transition: 'filter 0.2s ease',
        '&:hover': isDiagramInteractive ? {
          '& .delete-button': { opacity: 1 },
          '& .edit-splitter-button': { opacity: 1 },
        } : {},
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
      >
        <polygon points={trianglePoints} fill={handleColor} fillOpacity={0.12} />
        <polygon points={trianglePoints} fill="#ffffff" fillOpacity={0.55} />
        <polygon
          points={trianglePoints}
          fill="none"
          stroke={strokeColor}
          strokeWidth={selected ? 1.8 : 1.4}
          vectorEffect="non-scaling-stroke"
        />
      </Box>

      {isDiagramInteractive && (
      <>
      <Tooltip title="Editar splitter" arrow>
        <IconButton
          className="edit-splitter-button"
          onClick={handleEditPadrao}
          size="small"
          sx={{
            position: 'absolute',
            top: 2,
            left: '22%',
            backgroundColor: '#1976d2',
            color: '#fff',
            width: 17,
            height: 17,
            opacity: 0,
            zIndex: 12,
            '&:hover': { backgroundColor: '#1565c0' },
          }}
        >
          <EditIcon sx={{ fontSize: 10 }} />
        </IconButton>
      </Tooltip>

      <IconButton
        className="delete-button"
        onClick={handleDelete}
        size="small"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          backgroundColor: '#f44336',
          color: '#fff',
          width: 17,
          height: 17,
          opacity: 0,
          zIndex: 12,
          '&:hover': { backgroundColor: '#d32f2f' },
        }}
      >
        <DeleteIcon sx={{ fontSize: 10 }} />
      </IconButton>
      </>
      )}

      {/* Entrada — ponta do triângulo (ápice) */}
      <Box
        sx={{
          position: 'absolute',
          top: `${apexPct}%`,
          left: HANDLE_OFFSET,
          transform: 'translateY(-50%)',
          width: HANDLE_PX,
          height: HANDLE_PX,
          zIndex: 10,
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="input-0"
          isConnectable={!isHandleConnected('input-0', 'target')}
          style={{
            ...handleBaseStyle,
            background: handleColor,
            border: '2px solid #fff',
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'none',
          }}
        />
      </Box>      {data.opticalSignal?.inputSignal != null && (
        <Typography
          sx={{
            position: 'absolute',
            top: `${apexPct}%`,
            left: 10,
            transform: 'translateY(-50%)',
            fontSize: '0.5rem',
            fontWeight: 700,
            color: '#d32f2f',
            backgroundColor: 'rgba(255,255,255,0.9)',
            px: 0.3,
            borderRadius: '2px',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          {data.opticalSignal.inputSignal.toFixed(2)} dBm
        </Typography>
      )}

      {/* Saídas — base direita, número e bolinha na mesma linha */}
      {Array.from({ length: outputsCount }, (_, index) => {
        const topPx = getOutputTopPx(index);
        const handleId = `output-${index}`;
        const numeroSaida = index + 1;
        const signalOut = data.opticalSignal?.outputSignals?.[handleId];
        const { cor: corHandle, isBranco } = getCorHandleSaida(index);

        return (
          <Box
            key={handleId}
            sx={{
              position: 'absolute',
              top: topPx,
              right: 0,
              transform: 'translateY(-50%)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '4px',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.52rem',
                fontWeight: 600,
                color: '#555',
                lineHeight: 1,
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {numeroSaida}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                right: HANDLE_OFFSET,
                width: HANDLE_PX,
                height: HANDLE_PX,
                flexShrink: 0,
              }}
            >
              <Handle
                type="source"
                position={Position.Right}
                id={handleId}
                isConnectable={!isHandleConnected(handleId, 'source')}
                style={{
                  ...handleBaseStyle,
                  background: corHandle,
                  border: isBranco ? '2px solid #ccc' : '2px solid #fff',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: 'none',
                }}
              />
            </Box>            {signalOut != null && (
              <Typography
                sx={{
                  position: 'absolute',
                  left: 'calc(100% + 4px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.48rem',
                  fontWeight: 700,
                  color: '#4caf50',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  px: 0.3,
                  borderRadius: '2px',
                  whiteSpace: 'nowrap',
                }}
              >
                {signalOut.toFixed(2)} dBm
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Centro — chip + padrão */}
      <Box
        sx={{
          position: 'absolute',
          left: '42%',
          right: '22%',
          top: `${apexPct}%`,
          transform: 'translateY(-50%)',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.4,
          pointerEvents: 'none',
        }}
      >
        <Chip
          label={data.config}
          size="small"
          sx={{
            backgroundColor: handleColor,
            color: '#fff',
            fontSize: '0.62rem',
            height: 17,
            fontWeight: 700,
            pointerEvents: 'auto',
            '& .MuiChip-label': { px: 0.5 },
          }}
        />

        {usaPadraoFibra && (
          <Typography sx={{ fontSize: '0.52rem', color: '#888', lineHeight: 1.1, fontWeight: 500 }}>
            {getPadraoCoresSplitterLabel(padraoCores)}
          </Typography>
        )}

        {is1x2 && (
          <Typography sx={{ fontSize: '0.52rem', color: '#666', lineHeight: 1.1 }}>
            {data.saida1_porcentagem || 50}% / {100 - (data.saida1_porcentagem || 50)}%
          </Typography>
        )}
      </Box>    </Box>
  );
};

export default memo(SplitterNode);
